// =============================================
// PÁGINA: LISTA DE AUTOMAÇÕES WHATSAPP
// =============================================
// Arquivo: AutomationList.tsx
// Descrição: Página para listar e gerenciar fluxos de automação
// =============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import MetricsDashboard from '../../components/Automation/MetricsDashboard';
import FlowMonitoring from '../../components/Automation/FlowMonitoring';
import AuditLogViewer from '../../components/Automation/AuditLogViewer';
import './AutomationList.css';

type TabType = 'flows' | 'metrics' | 'monitoring' | 'audit';

// =============================================
// INTERFACES
// =============================================

interface AutomationFlow {
  id: number;
  nome: string;
  descricao?: string;
  ativo: boolean;
  trigger_type: string;
  total_execucoes?: number;
  execucoes_ativas?: number;
  criado_em: string;
  atualizado_em: string;
}

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const AutomationList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('flows');

  // Hook de infinite scroll
  const {
    items: flows,
    isLoading: loading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset: refreshFlows
  } = useInfiniteScroll<AutomationFlow>({
    fetchFunction: async (page, limit) => {
      try {
        const params: any = {};
        if (searchTerm) params.search = searchTerm;
        if (filterActive !== null) params.ativo = filterActive;

        const response = await apiService.listAutomationFlows(params);

        if (response.success && response.data) {
          return {
            success: true,
            data: {
              items: response.data,
              pagination: {
                total: response.data.length
              }
            }
          };
        }

        throw new Error('Erro ao carregar fluxos de automação');
      } catch (err) {
        console.error('Erro ao carregar fluxos:', err);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar fluxos de automação'
        };
      }
    },
    itemsPerPage: 10,
    filters: { filterActive }
  });

  const [error] = useState<string | null>(null);

  // =============================================
  // HANDLERS
  // =============================================

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    refreshFlows();
  };

  const handleCreate = () => {
    navigate('/automacoes/novo');
  };

  const handleEdit = (id: number) => {
    navigate(`/automacoes/editar/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/automacoes/visualizar/${id}`);
  };

  const handleDuplicate = async (id: number) => {
    if (!window.confirm('Deseja duplicar este fluxo?')) return;

    try {
      const response = await apiService.duplicateAutomationFlow(id);
      if (response.success) {
        alert('Fluxo duplicado com sucesso!');
        refreshFlows();
      } else {
        alert('Erro ao duplicar fluxo');
      }
    } catch (err) {
      alert('Erro ao duplicar fluxo');
    }
  };

  const handleToggleActive = async (id: number, currentStatus: boolean) => {
    try {
      const response = await apiService.updateAutomationFlow(id, {
        ativo: !currentStatus
      });

      if (response.success) {
        refreshFlows();
      } else {
        alert('Erro ao atualizar status do fluxo');
      }
    } catch (err) {
      alert('Erro ao atualizar status do fluxo');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este fluxo? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      const response = await apiService.deleteAutomationFlow(id);
      if (response.success) {
        alert('Fluxo excluído com sucesso!');
        refreshFlows();
      } else {
        alert('Erro ao excluir fluxo');
      }
    } catch (err) {
      alert('Erro ao excluir fluxo');
    }
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="automation-list-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <h1>
              <i className="fas fa-robot"></i> Automações WhatsApp
            </h1>
            <p>Gerencie fluxos de automação de checklist via WhatsApp</p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="fas fa-plus"></i> Novo Fluxo
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="automation-tabs">
        <button
          className={`tab-btn ${activeTab === 'flows' ? 'active' : ''}`}
          onClick={() => setActiveTab('flows')}
        >
          <i className="fas fa-project-diagram"></i>
          <span>Fluxos</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('metrics')}
        >
          <i className="fas fa-chart-line"></i>
          <span>Métricas</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'monitoring' ? 'active' : ''}`}
          onClick={() => setActiveTab('monitoring')}
        >
          <i className="fas fa-heartbeat"></i>
          <span>Monitoramento</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
          onClick={() => setActiveTab('audit')}
        >
          <i className="fas fa-history"></i>
          <span>Auditoria</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'metrics' && (
        <div className="tab-content metrics-tab">
          <MetricsDashboard />
        </div>
      )}

      {activeTab === 'monitoring' && (
        <div className="tab-content monitoring-tab">
          <FlowMonitoring logs={[]} />
        </div>
      )}

      {activeTab === 'audit' && (
        <div className="tab-content audit-tab">
          <AuditLogViewer />
        </div>
      )}

      {activeTab === 'flows' && (
      <>
      <div className="filters-section">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-group">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar fluxos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-secondary">Buscar</button>
        </form>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterActive === null ? 'active' : ''}`}
            onClick={() => setFilterActive(null)}
          >
            Todos
          </button>
          <button
            className={`filter-btn ${filterActive === true ? 'active' : ''}`}
            onClick={() => setFilterActive(true)}
          >
            Ativos
          </button>
          <button
            className={`filter-btn ${filterActive === false ? 'active' : ''}`}
            onClick={() => setFilterActive(false)}
          >
            Inativos
          </button>
        </div>
      </div>

      <div className="content-section">
        {loading && (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Carregando fluxos...</p>
          </div>
        )}

        {error && (
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button onClick={() => refreshFlows()} className="btn btn-secondary">
              Tentar Novamente
            </button>
          </div>
        )}

        {!loading && !error && flows.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-robot"></i>
            <h3>Nenhum fluxo encontrado</h3>
            <p>Crie seu primeiro fluxo de automação para começar</p>
            <button onClick={handleCreate} className="btn btn-primary">
              <i className="fas fa-plus"></i> Criar Primeiro Fluxo
            </button>
          </div>
        )}

        {!loading && !error && flows.length > 0 && (
          <>
            {/* Contador de resultados */}
            <ResultsCounter
              displayedItems={displayedItems}
              totalItems={totalItems}
              isLoading={loading}
              label="fluxos"
            />

            <div className="flows-grid">
            {flows.map((flow) => (
              <div key={flow.id} className="flow-card">
                <div className="card-header">
                  <div className="card-title">
                    <h3>{flow.nome}</h3>
                    <span className={`status-badge ${flow.ativo ? 'active' : 'inactive'}`}>
                      {flow.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </div>
                  <div className="card-actions">
                    <button
                      className="icon-btn"
                      title={flow.ativo ? 'Desativar' : 'Ativar'}
                      onClick={() => handleToggleActive(flow.id, flow.ativo)}
                    >
                      <i className={`fas ${flow.ativo ? 'fa-pause' : 'fa-play'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  {flow.descricao && <p className="description">{flow.descricao}</p>}

                  <div className="card-stats">
                    <div className="stat-item">
                      <i className="fas fa-play-circle"></i>
                      <span>{flow.total_execucoes || 0} execuções</span>
                    </div>
                    <div className="stat-item">
                      <i className="fas fa-clock"></i>
                      <span>{flow.execucoes_ativas || 0} em andamento</span>
                    </div>
                  </div>

                  <div className="card-meta">
                    <span>
                      <i className="fas fa-bolt"></i> {flow.trigger_type}
                    </span>
                    <span>
                      <i className="fas fa-calendar"></i>{' '}
                      {new Date(flow.criado_em).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleView(flow.id)}
                  >
                    <i className="fas fa-eye"></i> Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEdit(flow.id)}
                  >
                    <i className="fas fa-edit"></i> Editar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDuplicate(flow.id)}
                  >
                    <i className="fas fa-copy"></i> Duplicar
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(flow.id)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
            </div>
            <LoadMoreButton
              onClick={loadMore}
              isLoading={loading}
              hasMore={hasMore}
            />
          </>
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default AutomationList;
